// ============================================================
// TMR Portal Admin — Taxas da TMR (rota /taxas): o molde global
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Notice } from '../../components/Notice';
import { PageHeader } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { bandLabel, formatRateValue, syncSummary } from '../../data/rateFormat';
import {
  useDeleteTmrRate,
  useSyncTmrRates,
  globalRateExportPage,
  useTmrRates,
} from '../../data/globalRates/queries';
import { isAwaitingLegacy, tmrRateTitle } from '../../data/globalRates/status';
import type { TmrRate, TmrRateSyncResult } from '../../data/globalRates/contract';
import { ExportMenu } from '../../components/ExportMenu';
import { GLOBAL_RATE_EXPORT_COLUMNS } from './exportColumns';
import { TmrRateFormModal } from './TmrRateFormModal';

/**
 * The TMR's global rate template — the first screen in any version of the TMR from which it can be managed.
 *
 * It is not a portfolio view of what Empresas are charged. It is `taxas_tmr`: the seven-line template every
 * new Empresa's rates are copied from, which until now was edited only by the ScriptCase back-office and,
 * before that, by nobody at all. Three things about it are load-bearing:
 *
 * - **Nothing propagates by default.** Adding a line reaches future Empresas only, and editing one reaches
 *   existing Empresas solely when the operator says so, after seeing the count (`15` §12, 2026-08-14).
 * - **The unit comes from the rate type.** This is the one screen that lists every type side by side, so a
 *   blanket `%` would put a wrong label over the two types that are amounts in reais (`15` §5).
 * - **An empty list under `needsImport` means "not brought over", not "no template".** Bringing it over is
 *   a write, so it is the manage-gated sync rather than a side effect of reading (`15` D-A2).
 */
export function GlobalRatesPage() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TmrRate | null>(null);
  const [deleting, setDeleting] = useState<TmrRate | null>(null);
  const [syncResult, setSyncResult] = useState<TmrRateSyncResult | null>(null);
  // What the last save did, in the operator's terms — it is the only place a queued propagation is
  // reported, and the modal that knows about it is gone by then.
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const canRead = usePermission(PERMISSIONS.ratesGlobalRead);
  const canManage = usePermission(PERMISSIONS.ratesGlobalManage);

  const query = useTmrRates(canRead);
  const sync = useSyncTmrRates();
  const remove = useDeleteTmrRate();

  // The template lives in the legacy and is materialized here on first use. Once per mount: a failure must
  // not become a loop, and the operator still has the button.
  const autoSynced = useRef(false);
  const needsImport = query.data?.needsImport ?? false;
  useEffect(() => {
    if (!needsImport || !canManage || autoSynced.current) return;
    autoSynced.current = true;
    void sync.mutateAsync(undefined).then(setSyncResult).catch(() => undefined);
  }, [needsImport, canManage, sync]);

  // The route is already gated (Portal.tsx), so this is the operator who reached it by URL. Saying so beats
  // an error state that reads like a fault — the server is right to refuse (06 §6).
  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver as taxas da TMR."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  const items = query.data?.items ?? [];

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Plataforma"
        title="Taxas da TMR"
        desc="O molde do qual as taxas de cada empresa nova são copiadas. Mudar aqui não altera, sozinho, o que as empresas atuais pagam."
        actions={
          <>
            <ExportMenu
              spec={{
                fileName: 'taxas-tmr',
                columns: GLOBAL_RATE_EXPORT_COLUMNS,
                fetchPage: globalRateExportPage(),
              }}
              totalCount={query.data?.items.length ?? 0}
              disabled={query.isPending || query.isError}
            />
            {canManage && !query.isPending && !query.isError && (
              <Btn variant="primary" icon={Icons.plus} onClick={() => setCreating(true)}>
                Nova taxa
              </Btn>
            )}
          </>
        }
      />

      {query.isPending ? (
        <Skeleton rows={6} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          {/* No legacy in this environment: the template can be edited here, but nothing reaches
              `taxas_tmr` and no propagation can be counted or carried out. */}
          {!query.data.legacyAvailable && (
            <Notice tone="warning">
              Este ambiente não tem conexão com o sistema legado. O molde pode ser editado aqui, mas não é
              replicado nem propagado enquanto isso não existir.
            </Notice>
          )}

          {query.data.needsImport && !canManage && !sync.isPending && (
            <Notice tone="warning">
              O molde ainda não foi trazido do sistema legado — a lista abaixo estar vazia não significa que
              não exista molde. Quem tem permissão para gerenciar as taxas da TMR pode trazê-lo pelo botão
              “Sincronizar com o legado”.
            </Notice>
          )}

          {query.data.needsImport && canManage && sync.isPending && (
            <Notice tone="neutral">Trazendo o molde do sistema legado…</Notice>
          )}

          {syncResult && !sync.isPending && <Notice tone="neutral">{syncSummary(syncResult)}</Notice>}

          {saveNotice && <Notice tone="success">{saveNotice}</Notice>}

          {sync.error && (
            <Notice tone="danger">
              {sync.error instanceof ApiError ? sync.error.detail : 'Não foi possível sincronizar o molde.'}
            </Notice>
          )}

          {remove.error && (
            <Notice tone="danger">
              {remove.error instanceof ApiError ? remove.error.detail : 'Não foi possível excluir a taxa.'}
            </Notice>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
              {items.length === 1 ? '1 taxa no molde' : `${items.length} taxas no molde`}
            </span>

            {canManage && query.data.legacyAvailable && (
              <Btn
                variant="secondary"
                icon={Icons.swap}
                onClick={() => void sync.mutateAsync(undefined).then(setSyncResult)}
                disabled={sync.isPending}
              >
                {sync.isPending ? 'Sincronizando…' : 'Sincronizar com o legado'}
              </Btn>
            )}
          </div>

          {items.length === 0 ? (
            <div className="pa-card">
              <EmptyState
                message={
                  query.data.needsImport
                    ? 'O molde ainda não foi trazido do sistema legado.'
                    : 'O molde global está vazio.'
                }
                hint={
                  query.data.needsImport
                    ? undefined
                    : 'Sem molde, uma empresa nova nasce sem nenhuma taxa. Cadastre as linhas que toda empresa deve receber.'
                }
                action={
                  canManage && !query.data.needsImport ? (
                    <Btn variant="primary" icon={Icons.plus} onClick={() => setCreating(true)}>
                      Nova taxa
                    </Btn>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
              {items.map((rate) => (
                <TmrRateRow
                  key={rate.id}
                  rate={rate}
                  canManage={canManage}
                  onEdit={() => setEditing(rate)}
                  onDelete={() => setDeleting(rate)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {(creating || editing) && (
        <TmrRateFormModal
          rate={editing}
          onClose={(summary) => {
            setEditing(null);
            setCreating(false);
            if (summary) setSaveNotice(summary);
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Excluir esta taxa do molde?"
          message={
            <>
              {tmrRateTitle(deleting)} · {bandLabel(deleting)}. As empresas que já copiaram esta linha
              mantêm a taxa delas — isto decide com o que uma empresa <strong>nova</strong> nasce, não o que
              as atuais pagam.
            </>
          }
          confirmLabel="Excluir do molde"
          danger
          icon={Icons.alert}
          onConfirm={() => remove.mutateAsync(deleting.id)}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

/** Props for {@link TmrRateRow}. */
interface TmrRateRowProps {
  rate: TmrRate;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

/** One line of the template: what it is, what it charges, and whether the legacy agrees. */
function TmrRateRow({ rate, canManage, onEdit, onDelete }: TmrRateRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        padding: '14px 18px',
        borderBottom: '1px solid var(--border-subtle)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 240, flex: '1 1 320px' }}>
        {/* A type the catalogue could not name reads as "Tipo N" rather than as a blank — the id is what
            the legacy is searched with, and the catalogue has been wrong before (15 D-A10). */}
        <div style={{ fontSize: 14.5, fontWeight: 550 }}>{tmrRateTitle(rate)}</div>

        <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 4 }}>
          {bandLabel(rate)}
          {rate.creditEngineTypeName ? ` · ${rate.creditEngineTypeName}` : ''}
        </div>

        {/* The template is copied into every Empresa, so an ambiguous line here becomes a thousand
            ambiguous rates out there — the legacy resolves an overlap by whichever row it finds first. */}
        {rate.overlapping && (
          <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 4 }}>
            <StrokeIcon d={Icons.alert} size={13} /> Outra linha deste tipo cobre parte da mesma faixa de
            parcelas — toda empresa nova herda a ambiguidade.
          </div>
        )}
      </div>

      <div style={{ textAlign: 'right', minWidth: 150 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 17, letterSpacing: '-0.01em' }}>
          {formatRateValue(rate.value, rate.rateTypeId)}
        </div>
        {isAwaitingLegacy(rate) && (
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>Aguardando sincronia</div>
        )}
      </div>

      {canManage && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Btn variant="ghost" icon={Icons.pen} onClick={onEdit}>
            Editar
          </Btn>
          <Btn variant="ghost" icon={Icons.x} onClick={onDelete}>
            Excluir
          </Btn>
        </div>
      )}
    </div>
  );
}
